import { useEffect } from "react";
import CopyToClipboard from "react-copy-to-clipboard";
import ReactGA from "react-ga4";
import { AiOutlineAndroid, AiOutlineApple } from "react-icons/ai";
import { LuCopy, LuGlobe, LuLoaderCircle, LuMonitor } from "react-icons/lu";
import { typography } from "../../design/tokens";
import {
  BuildPlatform,
  useProjectBuildActions,
  useProjectBuildsAndroid,
  useProjectBuildsStore,
  useProjectBuildsWeb,
} from "../../core_stores/useProjectBuildsStore";
import { useSnackbar } from "../../providers/SnackbarProvider";
import { trackEvent } from "../../utils/analyticsUtils";
import {
  useDashboardControlsActions,
  useSelectedPublishPlatform,
} from "../../view_stores/useDashboardControlsStore";
import ProjectPublishAvailablePlatforms from "./components/ProjectPublishAvailablePlatforms";
import ProjectPublishBuilds from "./components/ProjectPublishBuilds";
import ProjectPublishStartBuild from "./components/ProjectPublishStartBuild";

export default function ProjectPublish() {
  const selectedPublishPlatform = useSelectedPublishPlatform();

  const { loadBuilds } = useProjectBuildActions();
  const { setPublishPlatform } = useDashboardControlsActions();

  const platforms = [
    {
      type: BuildPlatform.WEB,
      label: "Web",
      icon: <LuGlobe size={32} />,
      enabled: true,
    },
    {
      type: BuildPlatform.ANDROID,
      label: "Android",
      icon: <AiOutlineAndroid size={32} />,
      enabled: true,
    },
    {
      type: BuildPlatform.IOS,
      label: "iOS",
      icon: <AiOutlineApple size={32} />,
      enabled: false,
    },
    {
      type: BuildPlatform.DESKTOP,
      label: "Desktop",
      icon: <LuMonitor size={32} />,
      enabled: false,
    },
  ];

  const handlePlatformChange = (platformType: BuildPlatform) => {
    setPublishPlatform(platformType);
    if (platformType === BuildPlatform.WEB) {
      const webBuilds = useProjectBuildsStore.getState().web;
      if (webBuilds === null) {
        loadBuilds(platformType, false);
      }
    } else if (platformType === BuildPlatform.ANDROID) {
      const androidBuilds = useProjectBuildsStore.getState().android;
      if (androidBuilds === null) {
        loadBuilds(platformType, false);
      }
    }
  };

  useEffect(() => {
    ReactGA.send({ hitType: "pageview", title: "Workspace Publish" });
    trackEvent("publish_opened");
    loadBuilds(selectedPublishPlatform, false);
  }, []);

  return (
    <div className="h-full flex flex-col gap-8 px-4 md:px-8 pt-6 max-w-full md:max-w-[1000px] mx-auto">
      <ProjectPublishAvailablePlatforms
        allPlatforms={platforms.filter(p => p.type !== BuildPlatform.DESKTOP)}
        selectedPlatform={selectedPublishPlatform}
        onPlatformSelected={handlePlatformChange}
      />

      <Content selectedPlatform={selectedPublishPlatform} />
    </div>
  );
}

function Content({ selectedPlatform }: { selectedPlatform: BuildPlatform }) {
  const platformBuilds =
    selectedPlatform === BuildPlatform.WEB
      ? useProjectBuildsWeb()
      : useProjectBuildsAndroid();

  if (platformBuilds === null) {
    return (
      <div className="flex justify-center mt-5">
        <LuLoaderCircle size={32} className="animate-spin" />
      </div>
    );
  }

  return (
    <div className="overflow-y-auto flex flex-col gap-8" id="builds-container">
      <ProjectPublishStartBuild selectedPlatform={selectedPlatform} />

      {platformBuilds.builds && platformBuilds.builds?.length > 0 && (
        <div className="flex flex-col gap-8">
          {selectedPlatform === BuildPlatform.WEB && <WebPreviewUrl />}
          <ProjectPublishBuilds selectedPlatform={selectedPlatform} />
        </div>
      )}
    </div>
  );
}

function WebPreviewUrl() {
  const webBuilds = useProjectBuildsWeb();
  const webPreviewUrl = webBuilds?.previewUrl;
  const { showSnackbar } = useSnackbar();

  const isLoading = webPreviewUrl === null;

  const handleCopy = () => {
    showSnackbar("Copied to clipboard");
  };

  return (
    <div className="flex flex-col gap-4 overflow-hidden">
      <span className={`${typography.size.sm} font-medium`}>App URL</span>
      <div
        className={`rounded-lg
      flex justify-between items-center
      h-[50px] px-4
      text-dark-placeholder ${typography.size.sm}
    border border-dark-border`}
      >
        {isLoading ? (
          <span>Loading...</span>
        ) : (
          <>
            <a
              href={webPreviewUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="underline"
            >
              {webPreviewUrl}
            </a>
            <CopyToClipboard text={webPreviewUrl ?? ""} onCopy={handleCopy}>
              <button
                className="hover:opacity-50
            transition-opacity duration-300"
              >
                <LuCopy size={16} />
              </button>
            </CopyToClipboard>
          </>
        )}
      </div>
    </div>
  );
}
